import React from 'react'
import { Box, Text } from '@react-three/drei'
import { useDesignStore, type ADUModel, type ADUSection } from '../../../store/designStore'

interface ADUModularModelProps {
  adu: ADUModel
  isSelected?: boolean
  isDragging?: boolean
  onClick?: () => void
  onPointerDown?: (event: any) => void
}

const ADUModularModel: React.FC<ADUModularModelProps> = ({
  adu,
  isSelected = false,
  isDragging = false,
  onClick,
  onPointerDown
}) => {
  const { selectedObjects } = useDesignStore()

  // If not modular or no sections, render as single unit
  if (!adu.isModular || adu.sections.length === 0) {
    return (
      <group position={adu.position} rotation={adu.rotation} scale={adu.scale}>
        <Box
          args={[adu.dimensions.width, adu.dimensions.height, adu.dimensions.depth]}
          onClick={onClick}
          onPointerDown={onPointerDown}
        >
        <meshStandardMaterial 
          color={adu.materials.walls} 
          transparent={isDragging}
          opacity={isDragging ? 0.8 : 1}
        />
        </Box>
        
        {/* Selection outline */}
        {isSelected && (
          <Box args={[adu.dimensions.width + 0.2, adu.dimensions.height + 0.2, adu.dimensions.depth + 0.2]}>
            <meshBasicMaterial 
              color={isDragging ? "#ff6b6b" : "#4299e1"} 
              wireframe 
              transparent 
              opacity={0.8} 
            />
          </Box>
        )}
        
        {/* ADU Label */}
        <Text
          position={[0, adu.dimensions.height + 1, 0]}
          fontSize={1.5}
          color="#2d3748"
          anchorX="center"
          anchorY="middle"
        >
          {adu.name}
        </Text>
      </group>
    )
  }

  // Render modular sections
  return (
    <group position={adu.position} rotation={adu.rotation} scale={adu.scale}>
      {adu.sections.map((section, index) => (
        <ADUSectionModel
          key={section.id}
          section={section}
          aduId={adu.id}
          isSelected={isSelected}
          isDragging={isDragging}
          onClick={onClick}
          onPointerDown={onPointerDown}
        />
      ))}
      
      {/* Overall selection outline for the entire ADU */}
      {isSelected && (
        <group>
          {adu.sections.map((section) => (
            <Box 
              key={`outline-${section.id}`}
              args={[
                section.dimensions.width + 0.2, 
                section.dimensions.height + 0.2, 
                section.dimensions.depth + 0.2
              ]}
              position={section.position}
            >
              <meshBasicMaterial 
                color={isDragging ? "#ff6b6b" : "#4299e1"} 
                wireframe 
                transparent 
                opacity={0.5} 
              />
            </Box>
          ))}
        </group>
      )}
      
      {/* ADU Label */}
      <Text
        position={[0, Math.max(...adu.sections.map(s => s.dimensions.height)) + 1, 0]}
        fontSize={1.5}
        color="#2d3748"
        anchorX="center"
        anchorY="middle"
      >
        {adu.name} {adu.isModular ? '(Modular)' : ''}
      </Text>
    </group>
  )
}

// Individual section component
interface ADUSectionModelProps {
  section: ADUSection
  aduId: string
  isSelected?: boolean
  isDragging?: boolean
  onClick?: () => void
  onPointerDown?: (event: any) => void
}

const ADUSectionModel: React.FC<ADUSectionModelProps> = ({
  section,
  aduId,
  isSelected,
  isDragging,
  onClick,
  onPointerDown
}) => {
  const getRoomColor = (roomType?: string) => {
    const colors = {
      living: '#48bb78',      // Green
      kitchen: '#ed8936',     // Orange
      bedroom: '#4299e1',     // Blue
      bathroom: '#9f7aea',    // Purple
      dining: '#38b2ac',      // Teal
      office: '#f56565',      // Red
      storage: '#a0aec0',     // Gray
      utility: '#805ad5'      // Purple
    }
    return colors[roomType as keyof typeof colors] || section.materials.walls
  }

  return (
    <group position={section.position}>
      {/* Main structure */}
      <Box
        args={[section.dimensions.width, section.dimensions.height, section.dimensions.depth]}
        onClick={onClick}
        onPointerDown={onPointerDown}
      >
        <meshStandardMaterial 
          color={getRoomColor(section.roomType)} 
          transparent={isDragging}
          opacity={isDragging ? 0.8 : 1}
        />
      </Box>
      
      {/* Section label */}
      <Text
        position={[0, section.dimensions.height/2 + 0.5, section.dimensions.depth/2 + 0.1]}
        fontSize={0.8}
        color="#2d3748"
        anchorX="center"
        anchorY="middle"
      >
        {section.name}
      </Text>
      
      {/* Room type indicator */}
      {section.roomType && (
        <Text
          position={[0, section.dimensions.height/2 - 0.5, section.dimensions.depth/2 + 0.1]}
          fontSize={0.6}
          color="#4a5568"
          anchorX="center"
          anchorY="middle"
        >
          {section.roomType.charAt(0).toUpperCase() + section.roomType.slice(1)}
        </Text>
      )}
    </group>
  )
}

export default ADUModularModel
